import React, { useCallback, useMemo, useState } from 'react';
import { BaseTextInputProps } from '../shared-types';
import { getGetIdFn } from '../utils';
import InputControl from '../InputControl/InputControl';
import PasswordInput from '../PasswordInput/PasswordInput';
import BaseInputIcon from '../BaseInput/BaseInputIcon';

export type Props = BaseTextInputProps & {
  componentName?: string;
  initialValue?: string;
};

export function removeNonDigits(input: string): string {
  return input.replace(/\D/g, '').substring(0, 9);
}

export function formatSSN(input: string): string {
  // Remove non-digits and limit to 9 characters
  const ssn = removeNonDigits(input);
  const parts = [];

  let i = 0;
  parts.push(ssn.substring(i, i + 3));
  if (ssn.length > 3) {
    i += 3;
    parts.push(ssn.substring(i, i + 2));
    if (ssn.length > 5) {
      i += 2;
      parts.push(ssn.substring(i, i + 4));
    }
  }
  return parts.join('-');
}

export function validateSSNInput(
  ssn: string,
  minLength = 9,
  maxLength = 9
): boolean {
  const digits = removeNonDigits(ssn);
  return digits.length >= minLength && digits.length <= maxLength;
}

const SSNInput: React.FC<Props> = ({
  onChange,
  disabled,
  error,
  label,
  showRequiredAsterisk,
  'aria-label': ariaLabel,
  readOnly,
  hint,
  onHintToggle,
  componentName = 'SSNInput',
  initialValue = '',
  'data-testid': dataTestID,
  eventId,
  ...inputProps
}: Props) => {
  const id = useMemo(
    () => getGetIdFn(componentName)(label),
    [componentName, label]
  );

  const [value, setValue] = useState(initialValue); // stripped out dashes, "raw" value
  const valueToDisplay = useMemo(() => formatSSN(value), [value]);

  const validInputIcon = useMemo(
    () =>
      validateSSNInput(value) ? (
        <BaseInputIcon
          aria-label="Valid"
          icon="checkmarkCircleInverted"
          color={{
            type: 'theme',
            colorLegacy: 'Green/50',
            colorNext: 'success/default',
          }}
        />
      ) : null,
    [value]
  );

  const handleChange = useCallback(
    (v: string) => {
      const newValue = removeNonDigits(v);
      setValue(newValue);
      if (onChange) {
        onChange(newValue);
      }
    },
    [onChange, setValue]
  );

  return (
    // TODO: remove <InputControl> as this is already contained in <PasswordInput>
    <InputControl
      label={label}
      id={id}
      showRequiredAsterisk={showRequiredAsterisk}
      errorMessage={error}
      eventId={eventId}
    >
      <PasswordInput
        {...inputProps}
        data-testid={dataTestID || id}
        readOnly={readOnly}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={handleChange}
        value={valueToDisplay}
        hint={hint}
        onHintToggle={onHintToggle}
        placeholder="123-45-6789"
        adornmentLeft={
          <BaseInputIcon
            icon="lock2"
            size={16}
            color={{
              type: 'theme',
              colorLegacy: 'Neutral/90',
              colorNext: 'neutral/default',
            }}
            aria-hidden
          />
        }
        adornmentRight={validInputIcon}
      />
    </InputControl>
  );
};

export default SSNInput;
